import React, { useState } from "react";
import { Megaphone, X, Loader2, ImagePlus, Pin, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCrypto } from "../context/CryptoContext";
import { encryptAndUpload } from "../lib/mediaUpload";

const inp = "w-full bg-[#0A0A0A] border border-white/10 focus:border-[#14A89A] px-3 py-2 text-white font-body text-sm outline-none";

const Preview = ({ headline, note, label, img }) => (
  <div className="border border-white/10 bg-[#0A0A0A] p-3" data-testid="share-hearth-preview">
    <div className="font-body text-[10px] uppercase tracking-wider text-[#14A89A] flex items-center gap-1 mb-1.5"><BookOpen size={11} /> New lesson</div>
    <div className="font-body text-white font-medium">{headline || "Headline"}</div>
    {note && <p className="font-body text-sm text-zinc-300 mt-1 whitespace-pre-wrap">{note}</p>}
    {img && <img src={img} alt="" className="mt-2 w-full max-h-40 object-cover rounded" />}
    <span className="mt-2.5 inline-block bg-[#14A89A] text-[#0A0A0A] font-semibold font-body text-xs px-3 py-1.5">{label || "Open lesson"}</span>
  </div>
);

const ShareForm = ({ it, handle, hearthId, onClose }) => {
  const { encryptNetText, encryptNetBytes, hasNetwork } = useCrypto();
  const [headline, setHeadline] = useState(`New lesson: ${it.title || "Untitled"}`);
  const [note, setNote] = useState("");
  const [label, setLabel] = useState("Open lesson");
  const [file, setFile] = useState(null);
  const [pin, setPin] = useState(false);
  const [busy, setBusy] = useState(false);
  const imgUrl = file ? URL.createObjectURL(file) : null;
  const post = async () => {
    if (!hasNetwork()) return toast.error("Unlock your vault first");
    if (!headline.trim()) return toast.error("Add a headline");
    setBusy(true);
    try {
      const cta = await encryptNetText(JSON.stringify({ headline: headline.trim(), label: label.trim() || "Open lesson", url: `/beacon/view/${handle}?lesson=${it.id}` }));
      const body = { kind: "lesson_share", ref: it.id, cta_ct: cta.ct, cta_iv: cta.iv, enc: true, media: [] };
      if (note.trim()) { const e = await encryptNetText(note.trim()); body.ct = e.ct; body.iv = e.iv; }
      if (file) body.media = [await encryptAndUpload(file, { encryptNetBytes, hasNetwork, onPct: () => {} })];
      const { data } = await api.post(`/hearths/${hearthId}/posts`, body);
      if (pin) await api.post(`/hearths/${hearthId}/posts/${data.id}/pin`).catch((e) => toast.error(e?.response?.data?.detail || "Posted, but couldn't pin"));
      toast.success("Shared to your Hearth");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't share"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2.5">
      <label className="block font-body text-[11px] uppercase tracking-wider text-zinc-500">Headline
        <input value={headline} onChange={(e) => setHeadline(e.target.value)} maxLength={140} data-testid="share-hearth-headline" className={`${inp} mt-1 normal-case tracking-normal`} /></label>
      <label className="block font-body text-[11px] uppercase tracking-wider text-zinc-500">Your note (optional)
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000} placeholder="Why this lesson matters, what to look for…" data-testid="share-hearth-note" className={`${inp} mt-1 resize-none normal-case tracking-normal`} /></label>
      <div className="flex gap-2">
        <label className="flex-1 block font-body text-[11px] uppercase tracking-wider text-zinc-500">Button text
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={30} data-testid="share-hearth-label" className={`${inp} mt-1 normal-case tracking-normal`} /></label>
        <label className="self-end flex items-center gap-1.5 border border-white/10 px-3 py-2 font-body text-xs text-zinc-300 cursor-pointer hover:bg-white/5" data-testid="share-hearth-image">
          <ImagePlus size={14} /> {file ? "Change image" : "Add image"}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
      </div>
      <label className="flex items-center gap-2 font-body text-xs text-zinc-300 cursor-pointer">
        <input type="checkbox" checked={pin} onChange={(e) => setPin(e.target.checked)} data-testid="share-hearth-pin" className="accent-[#14A89A]" /><Pin size={12} className="text-zinc-500" /> Pin to the top of the Hearth
      </label>
      <div className="font-body text-[11px] uppercase tracking-wider text-zinc-500 pt-1">Preview</div>
      <Preview headline={headline} note={note} label={label} img={imgUrl} />
      <p className="font-body text-[11px] text-zinc-500">Only the headline, note and image are shared. The lesson itself stays on your Beacon. Members who aren't subscribers see the lesson locked.</p>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onClose} className="font-body text-xs text-zinc-400 px-3 py-2">Cancel</button>
        <button onClick={post} disabled={busy} data-testid="share-hearth-post" className="bg-[#14A89A] text-[#0A0A0A] font-semibold font-body text-xs px-4 py-2 flex items-center gap-1.5 disabled:opacity-40">
          {busy && <Loader2 size={12} className="animate-spin" />} Share to Hearth
        </button>
      </div>
    </div>
  );
};

// Builder: share a lesson announcement to the creator's Hearth (customisable).
export const ShareToHearth = ({ it, handle }) => {
  const [hearthId, setHearthId] = useState(null);
  const [open, setOpen] = useState(false);
  const start = async () => {
    try {
      const { data } = await api.get("/beacon/hearth");
      if (!data.hearth?.id || data.hearth.enabled === false) return toast("Light your Hearth first. You'll find it further down this page.");
      setHearthId(data.hearth.id); setOpen(true);
    } catch { toast.error("Couldn't open your Hearth"); }
  };
  return (
    <>
      <button onClick={start} title="Share to Hearth" data-testid={`beacon-item-share-${it.id}`} className="text-zinc-600 hover:text-[#14A89A]"><Megaphone size={13} /></button>
      {open && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={() => setOpen(false)} data-testid="share-hearth-dialog">
          <div onClick={(e) => e.stopPropagation()} className="bg-[#121212] border border-white/10 w-full sm:max-w-md max-h-[90vh] overflow-y-auto p-5 relative fade-up">
            <button onClick={() => setOpen(false)} aria-label="Close" className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={18} /></button>
            <div className="font-body text-[10px] tracking-[0.25em] uppercase text-[#14A89A] mb-4 flex items-center gap-1.5"><Megaphone size={12} /> Share to your Hearth</div>
            <ShareForm it={it} handle={handle} hearthId={hearthId} onClose={() => setOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
};
